/**
 * HelpPill — WS-DISC-3 Global Help Menu trigger (Salesforce Lightning
 * style). 48x48 floating circular button pinned to the lower-right
 * viewport edge.
 *
 * Pattern: WAI-ARIA disclosure (not modal-dialog). The pill carries
 * `aria-expanded` mirroring the panel state and `aria-controls`
 * pointing at the panel id; background siblings stay interactive.
 *
 * Anchored at `--space-lg + env(safe-area-inset-bottom)` on every
 * breakpoint — the legacy mobile shift that cleared BottomNav is gone
 * with BottomNav itself (ad6f196). On iOS PWAs the safe-area-inset-
 * bottom env() keeps the pill above the home indicator. Z-index sits
 * above page content but below the modal overlay tier so a CmdK /
 * SpotlightTour open over the pill is still visually correct.
 */

import { HelpCircle } from 'lucide-react';
import './HelpPill.css';

type Props = {
    /** Mirrors the panel open state into `aria-expanded` so AT users
     *  hear the disclosure state. Spread from useHelpMenu.anchorProps. */
    'aria-expanded': boolean;
    /** Points at the panel id (disclosure pattern). */
    'aria-controls': string;
    /** Flip the panel. The hook owns the state; this is the trigger. */
    onClick: () => void;
};

export default function HelpPill({
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    onClick,
}: Props) {
    return (
        <button
            type="button"
            className="help-pill"
            aria-label="Open help menu"
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
            data-testid="help-pill"
            data-tour="help-pill"
            onClick={onClick}
        >
            <HelpCircle size={24} aria-hidden="true" />
        </button>
    );
}
